import { z } from "zod";
import { ContestEntryItemModel } from "@/lib/models/nolaContest";
import i18n from "@/lib/i18n";

export function generateEntryItemSchema(item: ContestEntryItemModel) {
  let schema: z.ZodType;
  switch (item.type) {
    case "string":
    case "title":
    case "synopsis": {
      let stringSchema = z.string();

      if (item.min != null) {
        stringSchema = stringSchema.min(
          item.min,
          i18n.t("common.validation.stringMinLength", { name: item.name, min: item.min }) as string
        );
      }
      if (item.max != null) {
        stringSchema = stringSchema.max(
          item.max,
          i18n.t("common.validation.stringMaxLength", { name: item.name, max: item.max }) as string
        );
      }
      if (item.regex != null) {
        stringSchema = stringSchema.regex(
          new RegExp(item.regex),
          i18n.t("common.validation.invalidFormat", { name: item.name }) as string
        );
      }

      schema = stringSchema;
      break;
    }
    case "number": {
      let numberSchema = z.number();

      if (item.isInt) {
        numberSchema = numberSchema.int(i18n.t("common.validation.integerRequired") as string);
      }
      if (item.min != null) {
        numberSchema = numberSchema.min(
          item.min,
          i18n.t("common.validation.numberMin", { name: item.name, min: item.min }) as string
        );
      }
      if (item.max != null) {
        numberSchema = numberSchema.max(
          item.max,
          i18n.t("common.validation.numberMax", { name: item.name, max: item.max }) as string
        );
      }

      schema = numberSchema;
      break;
    }
    case "select": {
      const selectSchema = z.enum(item.options as [string, ...string[]]);

      schema = selectSchema;
      break;
    }
    case "file": {
      const acceptExtensionsMap = {
        excel: [".xls", "xlsx"],
        csv: [".csv", ".tsv"],
        word: [".doc", ".docx"],
        pdf: [".pdf"],
      };
      const extensions = item.formats.map((f) => acceptExtensionsMap[f]).flat();

      const fileSchema = z.object({
        size: z
          .number()
          .max(
            item.maxSize,
            i18n.t("common.validation.fileSizeMax", { size: Math.floor(item.maxSize / (1024 * 1024)) }) as string
          ),
        name: z
          .string()
          .refine(
            (s) => extensions.some((ext) => s.endsWith(ext)),
            i18n.t("common.validation.fileFormatInvalid", { extensions: extensions.join(", ") }) as string
          ),
      });

      schema = fileSchema;
      break;
    }
    case "manuscripts": {
      let manuscriptSchema = z.array(z.string());

      // NOTE: 文字数での制限 (min/max) は src/pages/EventContestEntry.vue で実施している

      // 選択数の制限
      if (item.minItems)
        manuscriptSchema = manuscriptSchema.min(
          item.minItems,
          i18n.t("common.validation.itemsMin", { name: item.name, min: item.minItems }) as string
        );
      if (item.maxItems)
        manuscriptSchema = manuscriptSchema.max(
          item.maxItems,
          i18n.t("common.validation.itemsMax", { name: item.name, max: item.maxItems }) as string
        );

      schema = manuscriptSchema;
      break;
    }
    case "characters": {
      let characterSchema = z.array(z.string());

      // 選択数の制限
      if (item.minItems)
        characterSchema = characterSchema.min(
          item.minItems,
          i18n.t("common.validation.itemsMin", { name: item.name, min: item.minItems }) as string
        );
      if (item.maxItems)
        characterSchema = characterSchema.max(
          item.maxItems,
          i18n.t("common.validation.itemsMax", { name: item.name, max: item.maxItems }) as string
        );

      schema = characterSchema;
      break;
    }
    case "plot": {
      const plotSchema = z.string();

      schema = plotSchema;
      break;
    }
    case "worldviews":
    case "correlations":
      schema = z.any();
      break;
    case "nolaNovelCheckboxGroups":
      /**
       * TODO: 一旦anyで対応
       */
      schema = z.any();
      break;
    default:
      console.warn(`invalid item type: ${JSON.stringify(item)}`);
      throw new Error(`invalid item type: ${JSON.stringify(item)}`);
  }

  if (item.optional) schema = schema.nullish();

  return schema;
}

const errorMap: z.ZodErrorMap = (error, ctx) => {
  const requiredMessage = i18n.t("common.validation.required") as string;

  switch (error.code) {
    case z.ZodIssueCode.invalid_enum_value:
      return {
        message: i18n.t("common.validation.select") as string,
      };
    case z.ZodIssueCode.invalid_arguments:
      return {
        message: i18n.t("common.validation.invalidValue") as string,
      };
    case z.ZodIssueCode.too_small:
      if (error.minimum === 1 && error.type === "string") {
        return {
          message: requiredMessage,
        };
      }
      if (ctx.data == null) {
        return {
          message: requiredMessage,
        };
      }
      return {
        message: i18n.t("common.validation.minNumber", { min: error.minimum.toLocaleString() }) as string,
      };
    case z.ZodIssueCode.too_big:
      return {
        message: i18n.t("common.validation.maxNumber", { max: error.maximum.toLocaleString() }) as string,
      };
    case z.ZodIssueCode.invalid_type:
      if (error.expected === "object" && error.received === "null") {
        return {
          message: requiredMessage,
        };
      }
      return {
        message: i18n.t("common.validation.invalidValue") as string,
      };
    case z.ZodIssueCode.invalid_string:
      if (ctx.data === undefined) {
        return {
          message: requiredMessage,
        };
      }
      return {
        message: i18n.t("common.validation.invalidString") as string,
      };
    case z.ZodIssueCode.invalid_union:
      return {
        message: i18n.t("common.validation.invalidValue") as string,
      };
    default:
      break;
  }

  return { message: ctx.defaultError };
};

z.setErrorMap(errorMap);
